/**
 * FirmMonogram — the text mark a firm gets when it has no logo.
 *
 * BRD-05 (Paul, M1 §H): "if a firm has no logo, show the FIRM'S NAME, never the
 * Elreign 'E' placeholder." Until 2 Sep the rule was honoured on the
 * authenticator pad (`auth/brand-qr.tsx`) and nowhere else: both sidebars kept
 * drawing the Elreign letterform for every logo-less firm — which is every firm
 * on day one, before anyone has uploaded anything. The rule and its rendering
 * live here so the three surfaces share ONE copy; a second copy of a rule is
 * the copy that drifts.
 *
 * `monogramFor` — up to two initials from the first two SIGNIFICANT words, so
 * "Smith and Partners" reads SP rather than SA, "Van der Merwe" reads M, and
 * anything that is not a letter (digits, punctuation, "(Pty) Ltd") never starts
 * an initial. Returns '' when the name yields nothing usable, which every caller
 * reads as "there is no firm identity here" and falls back to the Elreign mark.
 *
 * Colour: the DERIVED brand primary, never a baked Elreign hex (ARC-02) — the
 * same contrast-checked token the rest of the app draws with.
 */

import type { ReactElement } from 'react';

const MONOGRAM_JOINERS = new Set(['and', 'the', 'of', 'for', 'de', 'van', 'der', 'du', 'la', 'le']);

export function monogramFor(name: string): string {
  const words = name
    .split(/[\s\-/&,.]+/)
    .map((w) => w.replace(/[^A-Za-zÀ-ɏ]/g, ''))
    .filter((w) => w.length > 0 && !MONOGRAM_JOINERS.has(w.toLowerCase()));
  if (words.length === 0) return '';
  if (words.length === 1) return words[0]!.slice(0, 1).toUpperCase();
  return (words[0]!.slice(0, 1) + words[1]!.slice(0, 1)).toUpperCase();
}

interface FirmMonogramProps {
  /** The firm's name; the initials are derived from it. */
  name: string;
  /** Rendered square size in CSS px. */
  size?: number;
  /**
   * `bare` — initials only, no tinted pad, and hidden from assistive tech.
   * The authenticator pad uses this: it sits inside a badge that is already
   * `aria-hidden` and supplies its own white ground.
   */
  bare?: boolean;
}

/** Renders nothing (not an empty pad) when the name yields no initials, so a
 *  caller can always write `firmName ? <FirmMonogram/> : <ReignMark/>` and
 *  still never show a blank square for a name made of punctuation. */
export function FirmMonogram({ name, size = 28, bare = false }: FirmMonogramProps): ReactElement | null {
  const initials = monogramFor(name);
  if (!initials) return null;
  const a11y = bare
    ? { 'aria-hidden': true as const }
    : { role: 'img' as const, 'aria-label': `${name.trim()} mark`, 'data-testid': 'firm-monogram' };
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="geometricPrecision"
      style={{ display: 'block', flex: '0 0 auto' }}
      {...a11y}
    >
      {!bare && (
        <rect
          x="0"
          y="0"
          width="32"
          height="32"
          rx="7"
          fill="var(--color-brand-primary, var(--primary-700))"
          opacity="0.12"
        />
      )}
      <text
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fill: 'var(--color-brand-primary, var(--primary-700))',
          fontFamily: 'var(--sans)',
          fontSize: initials.length > 1 ? 15 : 21,
          fontWeight: 600,
          letterSpacing: '-0.02em',
        }}
      >
        {initials}
      </text>
    </svg>
  );
}
